import React from "react";
import { Box, Chip, Stack } from "@mui/material";
import { DeleteOutlineOutlined } from "@mui/icons-material";

import CustomAlert from "../../../../../components/custom-alert";
import CustomCheckbox from "../../../../../components/custom-checkbox";
import TertiaryButton from "../../../../../components/buttons/tertiary-button";
import {
	LIABILITY_SOURCE_LABELS,
	includedMonthlyDebt,
	isCreditRowEdited,
	liabilitySource,
} from "../../../../../utils/current-loan-encompass-mappers";
import { VERBIAGE_CONSTANTS } from "../../../../../utils/verbiage.constants";

import styles from "./index.module.scss";

const formatCurrency = (amount) =>
	amount.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

/** "[n] of the liabilities you selected [is / are] linked … : [names]. Remove anyway?" */
export const linkedRemovalText = (linkedRows) => {
	const count = linkedRows.length;
	const names = linkedRows
		.map((row) => [row.holderName || "Liability", row.accountIdentifier].filter(Boolean).join(" · "))
		.join(", ");
	return `${VERBIAGE_CONSTANTS.REMOVE_LINKED_LIABILITIES_TITLE} ${count} of the liabilities you selected ${
		count === 1 ? "is" : "are"
	} linked to a property in Real Estate Owned: ${names}. Remove anyway?`;
};

/** Toolbar above the liabilities grid (PathA-2.06 / 2.09 / 2.14): select-all, Remove,
 * included-monthly-debt banner (DTI loans only) and the re-run-AURA note. Rows staged
 * for removal (`removed`) are not counted or selectable. */
const LiabilitiesReview = ({ rows, selected, onToggleAll, onRemove, isDSCR, showAuraNote }) => {
	const visible = (rows || []).map((row, idx) => ({ row, idx })).filter(({ row }) => !row?.removed);
	const allSelected = visible.length > 0 && visible.every(({ idx }) => selected.has(idx));
	const someSelected = visible.some(({ idx }) => selected.has(idx));

	return (
		<Box className={styles.review} data-testid="liabilities-review">
			{showAuraNote && (
				<CustomAlert
					severity="warning"
					subject={VERBIAGE_CONSTANTS.LIABILITIES_CHANGED_AFTER_AURA}
					extraClass={styles.note}
				/>
			)}
			{!isDSCR && (
				<Box className={styles.banner} data-testid="included-monthly-debt">
					Included monthly debt: <strong>{formatCurrency(includedMonthlyDebt(rows))}</strong>
				</Box>
			)}
			<Stack direction="row" alignItems="center" justifyContent="space-between" className={styles.toolbar}>
				<CustomCheckbox
					checked={allSelected}
					indeterminate={someSelected && !allSelected}
					handleChange={() => onToggleAll(!allSelected)}
					label="Select all"
					disabled={visible.length === 0}
				/>
				<TertiaryButton
					text="Remove"
					startIcon={<DeleteOutlineOutlined />}
					onClick={onRemove}
					disabled={!someSelected}
				/>
			</Stack>
		</Box>
	);
};

/** Per-row select checkbox, Source pill and (credit-report rows only) Edited pill. */
export const LiabilityRowTags = ({ row, checked, onToggle }) => {
	const source = liabilitySource(row);
	return (
		<Stack direction="row" alignItems="center" gap={1} className={styles.rowTags}>
			<CustomCheckbox checked={checked} handleChange={onToggle} label="Select" />
			<Chip size="small" label={`Source: ${LIABILITY_SOURCE_LABELS[source]}`} className={styles.sourcePill} />
			{isCreditRowEdited(row) && <Chip size="small" label="Edited" className={styles.editedPill} />}
		</Stack>
	);
};

export default LiabilitiesReview;
